import { aggregateMetrics, createEmptyMetrics } from '@shared/config/usageMetricsConfig';
import { formatAccountAge } from '@shared/utils/formatAccountAge';
import { useMemo } from 'react';
import type { WizardAdvisorUsageData } from '../types/metrics-type';

export function useAdvisorUsageMetrics(
  usageDetails: Record<string, UsageMetrics>,
  filteredAdvisors: AdvisorMetrics[],
) {
  const advisorUsageData = useMemo(
    () =>
      filteredAdvisors.reduce(
        (acc, advisor) => {
          const enrolledAt = advisor.advisorCreatedDateTime;
          const firstClientAddedAt = advisor.firstClientAddedAt;

          acc[advisor.advisorId] = {
            advisorId: advisor.advisorId,
            advisorName: `${advisor.advisorFirstName} ${advisor.advisorLastName}`,
            advisorEmail: advisor.advisorEmail || '',
            organizationName: advisor.organizationName || '',
            accountAge: formatAccountAge(advisor.advisorCreatedDateTime),
            usageMetrics: usageDetails[advisor.advisorId] || createEmptyMetrics(),
            enrolledAt,
            firstClientAddedAt,
            lastLoginAt: advisor.lastLogin,
            isWizardAdvisor: advisor.isWizardAdvisor,
            isNylPilotAdvisor: advisor.isNylPilotAdvisor,
          };
          return acc;
        },
        {} as Record<string, WizardAdvisorUsageData>,
      ),
    [filteredAdvisors, usageDetails],
  );

  const advisorsList = useMemo((): WizardAdvisorUsageData[] => {
    if (!advisorUsageData) {
      return [];
    }
    return Object.values(advisorUsageData);
  }, [advisorUsageData]);

  const activeAdvisorsCount = useMemo(() => {
    return advisorsList.filter(a => a.usageMetrics.totalAdvisorLogins > 0).length;
  }, [advisorsList]);

  const calculatedMetrics = useMemo(() => {
    if (!advisorUsageData) {
      return createEmptyMetrics();
    }
    const advisorsList = Object.values(advisorUsageData) as AdvisorUsageData[];
    return aggregateMetrics(advisorsList.map(a => a.usageMetrics));
  }, [advisorUsageData]);

  return {
    advisorUsageData,
    advisorsList,
    activeAdvisorsCount,
    calculatedMetrics,
  };
}
